/* Le contraste d'une paire de couleurs, selon WCAG 2.1.
 *
 * Une charte qui se contente de montrer ses couleurs ne dit pas si on peut
 * lire dessus. Le rapport de contraste, lui, le dit — et il se calcule, donc
 * il se vérifie. C'est la différence entre une affiche et un contrôle : la
 * page de charte mesure ses propres couleurs et annonce ce qui ne passe pas,
 * au lieu de laisser l'œil trancher un jour de bonne lumière.
 *
 * Les seuils de la norme : 4,5 pour du texte courant, 3 pour du grand texte
 * (18,66 px gras ou 24 px) et pour les contours d'un élément d'interface.
 */

export const SEUIL_TEXTE = 4.5;
export const SEUIL_GRAND = 3;

export interface Rvb { r: number; g: number; b: number }

/**
 * Lit une couleur CSS résolue. Le navigateur rend `rgb(…)` ou `rgba(…)` pour
 * une propriété calculée, mais une variable brute peut encore être du `#rrggbb`
 * — on accepte les deux plutôt que de supposer.
 */
export function lireCouleur(valeur: string): Rvb | null {
  const v = valeur.trim();

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(v);
  if (hex) {
    const h = hex[1] as string;
    const p = h.length === 3 ? [...h].map((c) => c + c) : [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)];
    return { r: parseInt(p[0] as string, 16), g: parseInt(p[1] as string, 16), b: parseInt(p[2] as string, 16) };
  }

  const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(v);
  if (rgb) {
    return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]) };
  }
  return null;
}

/** La luminance relative, canal par canal, avec la correction gamma de la norme. */
export function luminance({ r, g, b }: Rvb): number {
  const canal = (x: number) => {
    const c = x / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

/** Le rapport de contraste entre deux couleurs, de 1 (identiques) à 21. */
export function contraste(a: Rvb, b: Rvb): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [haut, bas] = la >= lb ? [la, lb] : [lb, la];
  return (haut + 0.05) / (bas + 0.05);
}

/** Le même, à partir de deux valeurs CSS. Null si l'une ne se lit pas. */
export function contrasteDe(avant: string, arriere: string): number | null {
  const a = lireCouleur(avant);
  const b = lireCouleur(arriere);
  if (!a || !b) return null;
  return Math.round(contraste(a, b) * 100) / 100;
}

/** Le verdict, en un mot, pour le seuil demandé. */
export function verdict(rapport: number | null, seuil = SEUIL_TEXTE): "ok" | "juste" | "non" | null {
  if (rapport == null) return null;
  if (rapport >= seuil) return rapport >= seuil + 2 ? "ok" : "juste";
  return "non";
}
